import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShieldCheck, MailWarning, LogOut, User, Mail, Calendar, Phone, Star } from 'lucide-react';

const ProfilePage = () => {
  const { user, logout, resendConfirmationEmail, loading } = useAuth();

  const handleResendEmail = async () => {
    if (resendConfirmationEmail) {
      await resendConfirmationEmail();
    }
  };

  // A verificação de 'loading' e '!user' acontece primeiro.
  // Isso garante que o código abaixo só execute quando 'user' for um objeto válido.
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-xl font-semibold text-foreground">Carregando perfil...</div>
      </div>
    );
  }

  // Como a verificação acima já foi feita, o TypeScript agora sabe que 'user' não é nulo.
  // Portanto, podemos declarar com segurança as constantes que dependem dele.
  const isVerified = user.status === 'true';

  return (
    <div className="container mx-auto py-8 px-4">

    {/*  
        temos que ter uma seção lateral que vai conter links para:
        Visão Geral,
        Transações,
        Historico,
        Definição de privacidade,
        Promoções

        Deve ser ajustado para dispositivos moveis
        cada componente deve ser renderizado em um container que vai ficar ao lado desse menu lateral
    */}
      <Card className="max-w-3xl mx-auto bg-card/80 backdrop-blur-sm border-border/40 shadow-lg">
        <CardHeader className="text-center">
          <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-primary/50 shadow-lg">
            <AvatarImage src={user.image || `https://avatar.vercel.sh/${user.email}.png`} alt={user.nome} />
            <AvatarFallback className="text-3xl">{user.nome.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <CardTitle className="text-3xl font-bold">{user.nome}</CardTitle>
          <CardDescription className="text-muted-foreground">{user.email}</CardDescription>
          <div className="flex justify-center mt-2">
            {isVerified ? (
              <Badge variant="success" className="flex items-center gap-1">
                <ShieldCheck className="h-4 w-4" />
                Conta Verificada
              </Badge>
            ) : (
              <Badge variant="destructive" className="flex items-center gap-1">
                <MailWarning className="h-4 w-4" />
                Não Verificada
              </Badge>
            )}
          </div>
          {/* Aqui vamos colocar um elemento de XP com estrelas que indicam cada nível 5/5 estrelas Star tamanho pequeno */}
        </CardHeader>

        <CardContent className="space-y-6">
          {!isVerified && (
            <Alert variant="destructive">
              <MailWarning className="h-4 w-4" />
              <AlertTitle>Verifique seu E-mail</AlertTitle>
              <AlertDescription className="flex flex-col md:flex-row md:items-center md:justify-between">
                <span>Sua conta ainda não foi verificada.</span>
                <Button 
                  onClick={handleResendEmail} 
                  variant="outline"
                  className="mt-2 md:mt-0 md:ml-4 bg-destructive-foreground text-destructive"
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Reenviar e-mail'}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-3 p-3 bg-background/50 rounded-md">
              <User className="h-5 w-5 text-primary" />
              <span className="text-muted-foreground">Usuário:</span>
              <span className="font-medium">{user.fullName}</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-background/50 rounded-md">
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium">{user.email}</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-background/50 rounded-md">
              <Phone className="h-5 w-5 text-primary" />
              <span className="text-muted-foreground">Telefone:</span>
              <span className="font-medium">{user.phone}</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-background/50 rounded-md">
              <Calendar className="h-5 w-5 text-primary" />
              <span className="text-muted-foreground">Membro desde:</span>
              <span className="font-medium">{user.createdAt}</span>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 text-foreground/90">Segurança</h3>
            {/* Também devemos ter funcionalidades para alterar `telefone` e `nome` */}
            <div className="p-4 bg-background/50 rounded-lg space-y-2">
              <Button variant="outline" className="w-full md:w-auto" disabled>Alterar Senha</Button>
              <p className="text-xs text-muted-foreground">
                Funcionalidade em breve
                {/* Aqui deve aparecer um formulário */}
              </p>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex justify-end">
          <Button variant="ghost" onClick={logout} className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProfilePage;
